import { Box, Checkbox, Flex, FormControl, FormLabel } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useToast } from '@/hooks/useToast';

import { FormControlError } from '@/components/FormControlError/FormControlError';
import { Button } from '@/components/UI/Buttons/Button';
import NavLink from '@/components/UI/Links/NavLink';

import { AgreementData, CompanyData } from '@/models/api/ProfileInfo';
import { AppService } from '@/services';
import { RepresentativeService } from '@/services/RepresentativeService';

type AgreementsProps = {
    agreements: AgreementData;
    representativeService: RepresentativeService;
    company?: CompanyData;
    agreementsCallback?: (userAgreements: AgreementData | null) => void;
};

export const Agreements = (props: AgreementsProps) => {
    const { agreements, representativeService, agreementsCallback } = props;
    const { privacyPolicyAccepted, rodoMarketingAccepted } = agreements;
    const { formState, handleSubmit, register, setValue, getValues } = useForm<AgreementData>({
        defaultValues: {
            privacyPolicyAccepted,
            rodoMarketingAccepted
        }
    });
    const { locale } = useRouter();
    const intl = useIntl();
    const { errorToast, infoToast } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const clubRegulationsFileLink = AppService.getClubRegulationsFileLink();
    const clubPrivacyPolicyFileLink = AppService.getClubPrivacyPolicyFileLink();
    const contactEmail = AppService.getContactEmailAddress();
    const informationObligationFileLink = AppService.getInformationObligationFileLink();
    const [isAgreementRequired, setIsAgreementRequired] = useState<boolean | undefined>(undefined);
    const [checkedItems, setCheckedItems] = useState([
        agreements.privacyPolicyAccepted,
        agreements.rodoMarketingAccepted
    ]);
    const obj = useMemo(
        () => ({
            intl,
            locale,
            representativeService,
            agreementsCallback
        }),
        [intl, locale, representativeService, agreementsCallback]
    );
    const dataStrong = useCallback(
        (messages: ReactNode[]) => (
            <Box as="strong" fontWeight="bold">
                {messages}
            </Box>
        ),
        []
    );
    const privacyPolicyFormattedMessage = useCallback(
        (messages: ReactNode[]) => {
            return (
                <NavLink href={clubPrivacyPolicyFileLink} isExternal variant="underlineBrand">
                    {messages}
                </NavLink>
            );
        },
        [clubPrivacyPolicyFileLink]
    );
    const dataInformationObligationFormattedMessage = useCallback(
        (messages: ReactNode[]) => {
            return (
                <NavLink href={informationObligationFileLink} isExternal variant="underlineBrand">
                    {messages}
                </NavLink>
            );
        },
        [informationObligationFileLink]
    );
    const dataClubRulesFormattedMessage = useCallback(
        (messages: ReactNode[]) => {
            return (
                <NavLink href={clubRegulationsFileLink} isExternal variant="underlineBrand">
                    {messages}
                </NavLink>
            );
        },
        [clubRegulationsFileLink]
    );
    const formSubmitHandler = useCallback<SubmitHandler<AgreementData>>(
        async (data) => {
            setIsSubmitting(() => true);

            try {
                await obj.representativeService.updateAgreements(data);

                if (obj.agreementsCallback) {
                    obj.agreementsCallback(data);
                } else {
                    infoToast({
                        description: obj.intl.formatMessage({ id: 'agreements-update-success' })
                    });
                }
            } catch (error) {
                errorToast({
                    description: obj.intl.formatMessage({ id: 'agreements-update-fail' })
                });
            }
            setIsSubmitting(() => false);
        },
        [obj, errorToast, infoToast]
    );
    const handleSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = e.target.checked;

        setCheckedItems(Array(5).fill(isChecked));

        const requiredCheckboxes = ['privacyPolicyAccepted'];
        const otherCheckboxes = ['rodoMarketingAccepted'];

        const values = getValues();

        if (isChecked) {
            [...requiredCheckboxes, ...otherCheckboxes].forEach((name) =>
                setValue(name as keyof AgreementData, true)
            );
        } else {
            requiredCheckboxes.forEach((name) =>
                setValue(name as keyof AgreementData, values[name as keyof AgreementData])
            );

            otherCheckboxes.forEach((name) => setValue(name as keyof AgreementData, false));
        }
    };
    const handleChange = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const updatedCheckedItems = [...checkedItems];

        updatedCheckedItems[index] = e.target.checked;
        setCheckedItems(updatedCheckedItems);
    };
    const formControls = useMemo(
        () => [
            {
                name: 'privacyPolicyAccepted',
                labelId: 'agreement-privacy-policy-text',
                required: true,
                checked: isAgreementRequired,
                readOnly: isAgreementRequired,
                values: {
                    strong: dataStrong,
                    'data-club-rules': dataClubRulesFormattedMessage,
                    'data-information-obligation': dataInformationObligationFormattedMessage,
                    'data-privacy-policy-link': privacyPolicyFormattedMessage
                }
            },
            {
                name: 'rodoMarketingAccepted',
                labelId: 'page.register.agreement-marketing',
                required: false,
                checked: undefined,
                readOnly: undefined,
                values: {
                    strong: dataStrong,
                    'data-contact-email': () => (
                        <NavLink
                            href={`mailto:${contactEmail}`}
                            isExternal
                            locale={false}
                            variant="underlineBrand"
                        >
                            {contactEmail}
                        </NavLink>
                    ),
                    'data-privacy-policy-link': privacyPolicyFormattedMessage
                }
            }
        ],
        [
            dataStrong,
            isAgreementRequired,
            contactEmail,
            dataClubRulesFormattedMessage,
            dataInformationObligationFormattedMessage,
            privacyPolicyFormattedMessage
        ]
    );

    useEffect(() => {
        setIsAgreementRequired(agreementsCallback ? undefined : true);
    }, [agreementsCallback]);

    return (
        <Flex
            as="form"
            mt={4}
            flexDirection="column"
            rowGap={2}
            noValidate
            onSubmit={handleSubmit(formSubmitHandler)}
        >
            <FormControl display="flex" alignItems="flex-start" columnGap={2}>
                <Checkbox
                    mt={1}
                    isChecked={checkedItems.every(Boolean)}
                    isIndeterminate={checkedItems.some(Boolean) && !checkedItems.every(Boolean)}
                    onChange={handleSelectAllChange}
                />
                <Box>
                    <FormLabel fontWeight="400" color="black">
                        <FormattedMessage id="page.register.check-all" />
                    </FormLabel>
                </Box>
            </FormControl>

            {formControls.map(({ name, labelId, required, checked, readOnly, values }, index) => {
                const isError = !!formState.errors[name as keyof AgreementData];
                const isChecked = checked || checkedItems[index];

                return (
                    <FormControl
                        key={name}
                        isInvalid={isError}
                        isRequired={required}
                        display="flex"
                        alignItems="flex-start"
                        columnGap={2}
                    >
                        <Checkbox
                            mt={1}
                            {...register(name as keyof AgreementData, {
                                required: { value: required, message: 'field-is-required' }
                            })}
                            isReadOnly={readOnly}
                            isChecked={isChecked}
                            onChange={handleChange(index)}
                        />
                        <Box>
                            <FormLabel fontWeight="400" color="black">
                                <FormattedMessage id={labelId} values={values} />
                            </FormLabel>
                            <FormControlError
                                formState={formState}
                                control={register(name as keyof AgreementData)}
                            ></FormControlError>
                        </Box>
                    </FormControl>
                );
            })}

            <Flex justifyContent="center" mt={4}>
                <Button type="submit" colorScheme="brand" isLoading={isSubmitting}>
                    <FormattedMessage id="save-data" />
                </Button>
            </Flex>
        </Flex>
    );
};
